import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppCompanyDomainDraft,
    clearAppCompanyDomainDraft,
    createAppCompanyDomainDraftPayload,
    NEW_APP_COMPANY_DOMAIN_DRAFT_KEY,
    readAppCompanyDomainDraft,
    saveAppCompanyDomainDraft,
} from './appCompanyDomainDraftUtils'

export default function NewAppCompanyDomain() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: 0 }
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState({
        companyId: 0,
        domain: '',
    })
    const [companyList, setCompanyList] = useState([])
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const initialDraftSnapshotRef = useRef('')

    const normalizeDomain = (domain) => (domain || '').trim().replace(/^@+/, '')
    const isValidDomain = (domain) => domain.includes('.') && !domain.includes('@')

    useEffect(() => {
        const baseParams = {
            companyId: 0,
            domain: '',
        }
        const storedDraft = readAppCompanyDomainDraft(NEW_APP_COMPANY_DOMAIN_DRAFT_KEY)
        const restoredDraft = applyAppCompanyDomainDraft({
            baseParams,
            draft: storedDraft,
        })

        initialDraftSnapshotRef.current = JSON.stringify(createAppCompanyDomainDraftPayload({
            params: baseParams,
        }))

        setParams((prev) => ({
            ...prev,
            ...restoredDraft.params,
        }))
        setDraftReady(true)
    }, [])

    // 기업 리스트 가져오기
    useEffect(() => {
        const getCompanyList = async () => {
            setLoading(true)

            try {
                const { data } = await API.post('/api/v1/admin/appCompany/getCompanyList', {})
                if (data.code === 200) {
                    setCompanyList(data.data)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('기업 목록을 불러오지 못했습니다.')
            } finally {
                setLoading(false)
            }
        }
        getCompanyList()
    }, [history])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 기업 도메인 등록은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppCompanyDomainDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCompanyDomainDraft(NEW_APP_COMPANY_DOMAIN_DRAFT_KEY)
            return
        }

        saveAppCompanyDomainDraft(NEW_APP_COMPANY_DOMAIN_DRAFT_KEY, nextDraft)
    }, [draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleUpdateField = (e) => {
        setParams((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const moveToList = (snackbarState) => {
        clearAppCompanyDomainDraft(NEW_APP_COMPANY_DOMAIN_DRAFT_KEY)
        history.push(createListLinkTarget({
            pathname: `/appCompanyDomains`,
            search: listSearch,
            curPage,
            searchText,
            searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message
            } : {})
        }))
    }

    const createData = async (payload) => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appCompany/createDomain', payload)
            if (data.code === 200) {
                // 등록 후에도 이전 기업 도메인 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업 도메인을 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextDomain = normalizeDomain(params.domain)

        setParams((prev) => ({
            ...prev,
            domain: nextDomain
        }))

        if (!params.companyId || Number(params.companyId) === 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업을 선택하세요.')
            return
        }

        if (!nextDomain || !isValidDomain(nextDomain)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인 형식이 잘못되었습니다.')
            return
        }

        createData({
            companyId: params.companyId,
            domain: nextDomain
        })
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>기업 도메인 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="기업"
                                    select
                                    id="companyId"
                                    name="companyId"
                                    value={params.companyId}
                                    size="small"
                                    sx={{ minWidth: 150 }}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                >
                                    <MenuItem value={0}>--- 선택하세요 ---</MenuItem>
                                    {companyList.map((item) => (
                                        <MenuItem key={item.companyId} value={item.companyId}>
                                            {item.companyName}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="도메인 @다음부터 입력해주세요"
                                    id="domain"
                                    name="domain"
                                    value={params.domain || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/appCompanyDomains"
                        search={listSearch}
                        visible={true}
                        curPage={curPage}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={() => clearAppCompanyDomainDraft(NEW_APP_COMPANY_DOMAIN_DRAFT_KEY)}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
            )}
        </Grid>
    )
}
